import { useEffect, useState, useRef } from "react";
import api from "@/lib/api";
import { ArrowRight, Droplets, Bot, Shield, ShieldCheck, GraduationCap, Wrench, Sparkles, Star, Zap } from "lucide-react";

// Map lucide icons for admin-configurable pubs.
const ICON_MAP = {
  Droplets, Bot, Shield, ShieldCheck, GraduationCap, Wrench, Sparkles, Star, Zap,
};

/**
 * Slot publicitaire rotatif — récupère 1 pub sur mount + auto-rotation toutes les 45s.
 * Envoie automatiquement les impressions et clicks en fire-and-forget.
 *
 * Props:
 *   placement: string — filtre côté serveur ("home" | "blog" | "model_detail" | "all")
 *   className: string — classes Tailwind additionnelles
 *   variant: "banner" (défaut, horizontal) | "compact" (mobile-first)
 */
export default function AdSlot({ placement = "all", className = "", variant = "banner" }) {
  const [ad, setAd] = useState(null);
  const [loading, setLoading] = useState(true);
  const [visible, setVisible] = useState(true); // Fade transition
  const prevAdIdRef = useRef(null);
  const timerRef = useRef(null);

  const trackImpression = (adId) => {
    if (!adId) return;
    const payload = { ad_id: adId, placement, source_page: window.location.pathname };
    try {
      if (navigator.sendBeacon) {
        const blob = new Blob([JSON.stringify(payload)], { type: "application/json" });
        const base = api.defaults.baseURL || "";
        navigator.sendBeacon(`${base}/ads/impression`, blob);
      } else {
        api.post("/ads/impression", payload).catch(() => {});
      }
    } catch (_e) { /* silent */ }
  };

  const trackClick = (adId) => {
    if (!adId) return;
    const payload = { ad_id: adId, placement, source_page: window.location.pathname };
    try {
      if (navigator.sendBeacon) {
        const blob = new Blob([JSON.stringify(payload)], { type: "application/json" });
        const base = api.defaults.baseURL || "";
        navigator.sendBeacon(`${base}/ads/click`, blob);
      } else {
        api.post("/ads/click", payload).catch(() => {});
      }
    } catch (_e) { /* silent */ }
  };

  const fetchAd = async () => {
    try {
      const params = { placement };
      if (prevAdIdRef.current) params.exclude = prevAdIdRef.current;
      const r = await api.get("/ads/serve", { params });
      if (r.data?.ad) {
        // Fade out then in
        setVisible(false);
        setTimeout(() => {
          setAd(r.data.ad);
          prevAdIdRef.current = r.data.ad.id;
          setVisible(true);
          trackImpression(r.data.ad.id);
        }, 250);
      } else {
        setAd(null);
      }
    } catch (_e) {
      setAd(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAd();
    // Auto-rotation toutes les 45 secondes
    timerRef.current = setInterval(fetchAd, 45000);
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [placement]);

  if (loading) {
    // Skeleton placeholder pour éviter le layout shift
    return (
      <div
        className={`w-full rounded-2xl bg-black/5 animate-pulse ${variant === "banner" ? "h-24 md:h-28" : "h-20"} ${className}`}
        data-testid={`ad-slot-loading-${placement}`}
      />
    );
  }

  if (!ad) return null;

  const Icon = ICON_MAP[ad.icon] || Sparkles;
  const bgStyle = ad.gradient_from && ad.gradient_to
    ? { background: `linear-gradient(135deg, ${ad.gradient_from} 0%, ${ad.gradient_to} 100%)` }
    : { background: "linear-gradient(135deg, #1e293b 0%, #0f172a 100%)" };

  return (
    <a
      href={ad.target_url}
      target="_blank"
      rel="noopener noreferrer nofollow sponsored"
      onClick={() => trackClick(ad.id)}
      className={`group relative block w-full rounded-2xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-500 ${
        visible ? "opacity-100" : "opacity-0"
      } ${className}`}
      style={bgStyle}
      data-testid={`ad-slot-${placement}`}
      data-ad-id={ad.id}
    >
      {/* Label "Publicité" — obligatoire pour la conformité */}
      <span
        className="absolute top-2 right-3 text-[9px] font-bold uppercase tracking-widest text-white/60 z-10"
        data-testid="ad-label"
      >
        Publicité
      </span>

      {/* Grain / noise overlay for depth */}
      <span className="absolute inset-0 bg-[radial-gradient(circle_at_20%_50%,rgba(255,255,255,0.15),transparent_60%)] pointer-events-none" />

      <div className={`relative flex items-center gap-4 ${variant === "banner" ? "px-6 py-5 md:px-8 md:py-6" : "px-4 py-3"}`}>
        {/* Icon block */}
        <div className={`shrink-0 grid place-items-center rounded-2xl bg-white/15 backdrop-blur-sm ${
          variant === "banner" ? "h-14 w-14 md:h-16 md:w-16" : "h-10 w-10"
        }`}>
          <Icon className={`text-white ${variant === "banner" ? "h-7 w-7 md:h-8 md:w-8" : "h-5 w-5"}`} />
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0 text-white">
          <div className={`font-display font-bold tracking-tight leading-tight ${
            variant === "banner" ? "text-lg md:text-xl" : "text-sm"
          }`} data-testid="ad-title">
            {ad.title}
          </div>
          {ad.subtitle && (
            <div className={`mt-1 text-white/85 line-clamp-2 ${variant === "banner" ? "text-sm" : "text-xs"}`}>
              {ad.subtitle}
            </div>
          )}
          <div className={`mt-2 flex items-center gap-2 text-[10px] uppercase tracking-widest text-white/60`}>
            <span>{ad.advertiser_name}</span>
          </div>
        </div>

        {/* CTA */}
        <div className={`shrink-0 hidden sm:flex items-center gap-2 rounded-full bg-white text-secondary font-bold group-hover:bg-primary group-hover:text-white transition-colors ${
          variant === "banner" ? "px-5 py-2.5 text-sm" : "px-3 py-1.5 text-xs"
        }`}>
          {ad.cta_label}
          <ArrowRight className={`transition-transform group-hover:translate-x-0.5 ${variant === "banner" ? "h-4 w-4" : "h-3 w-3"}`} />
        </div>
      </div>
    </a>
  );
}
